import { useState } from 'react';
import { resetPasswordAPI } from '@services/auth';
import useNotistack from '@hooks/useNotistack';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Btn from '@ui/Btn';
import LabeledFormInput from '@ui/LabeledFormInput';
import { flex } from '@styles/vars';
import clsx from 'clsx';
import styled from 'styled-components';
import { breakpoints } from '@styles/vars';

const StyledForm = styled.form`
  width: 100%;
  padding: 20px;
  display: flex;
  ${flex.col}
  ${breakpoints.laptop} {
    width: 70%;
  }
`;

const InputWrapper = styled.div`
  margin: 10px 0px;
`;

const BtnWrapper = styled.div`
  margin: 10px 0px;
`;

const resetPasswordSchema = yup.object({
  password: yup.string().required('This field is required'),
  confirm_password: yup
    .string()
    .required('This field is required')
    .oneOf([yup.ref('password'), null], 'Password did not match')
});

export default function ResetPasswordForm() {
  const [isLoading, setIsLoading] = useState(false);
  const { uid, token } = useParams();
  const { notify } = useNotistack();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      password: '',
      confirm_password: ''
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values, action) => {
      try {
        setIsLoading(true);
        const res = await resetPasswordAPI(values.password, uid, token);
        if (res.isOk) {
          notify('Password changed successfully', 'success');
          action.resetForm();
          navigate('/login');
        } else {
          const err = res.error;
          if (err && typeof err === 'object') {
            for (const [key, value] of Object.entries(err)) {
              notify(`${key}: ${value}`, 'error');
            }
          } else if (typeof err === 'string') {
            notify(err, 'error');
          } else {
            notify('Could not reset password', 'error');
          }
        }
      } finally {
        setIsLoading(false);
      }
    }
  });

  return (
    <StyledForm onSubmit={formik.handleSubmit}>
      <InputWrapper>
        <LabeledFormInput
          title="Password"
          placeholder="Password"
          type="password"
          name="password"
          onChange={formik.handleChange}
          value={formik.values.password}
          error={formik.errors.password}
          style={{ marginTop: '-25px' }}
        />
      </InputWrapper>
      <InputWrapper>
        <LabeledFormInput
          title="Confirm Password"
          placeholder="Confirm Password"
          type="password"
          name="confirm_password"
          onChange={formik.handleChange}
          value={formik.values.confirm_password}
          error={formik.errors.confirm_password}
        />
      </InputWrapper>
      <BtnWrapper>
        <Btn
          className={clsx(isLoading && 'disabled')}
          disabled={isLoading || !formik.isValid}
          type="submit"
          text={!isLoading ? 'Submit' : 'Please wait...'}
          style={{ marginTop: '18px' }}
        />
      </BtnWrapper>
    </StyledForm>
  );
}
